import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import debounce from "lodash.debounce";
import maplibregl, { type LngLatBoundsLike, type Map as MapLibreMap, type StyleSpecification } from "maplibre-gl";
import type { Layer } from "@deck.gl/core";
import { MapboxOverlay } from "@deck.gl/mapbox";
import { ScatterplotLayer, TextLayer } from "@deck.gl/layers";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import Supercluster from "supercluster";
import api from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import LoadingSplash from "@/components/LoadingSplash";
import "./ReportsCoverage.css";
import "maplibre-gl/dist/maplibre-gl.css";

type MapExtent = {
  min_lat: number;
  max_lat: number;
  min_lng: number;
  max_lng: number;
};

type MapPoint = {
  address_id: number;
  latitude: number;
  longitude: number;
  status: string;
  user_id: number;
  timestamp: string;
};

type PointProps = MapPoint & { visited: boolean };
type ClusterPoint = Supercluster.PointFeature<PointProps>;
type ClusterFeature = Supercluster.ClusterFeature<Supercluster.AnyProps>;
type ClusterLike = ClusterPoint | ClusterFeature;

const BASEMAP_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    // Swap the tiles array to use MapTiler/OpenFreeMap/etc.
    "raster-tiles": {
      type: "raster",
      tiles: ["https://tile.openstreetmap.org/{z}/{x}/{y}.png"],
      tileSize: 256,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
  },
  layers: [
    {
      id: "raster-tiles",
      type: "raster",
      source: "raster-tiles",
      minzoom: 0,
      maxzoom: 19,
    },
  ],
};

const HEATMAP_MAX_ZOOM = 9;
const CLUSTER_MAX_ZOOM = 13;

const VISITED_COLOR = [198, 40, 40, 200];
const NOT_VISITED_COLOR = [120, 120, 120, 200];
const CLUSTER_COLOR = [51, 105, 30, 200];

async function fetchMapExtent(): Promise<MapExtent> {
  const res = await api.get("/map/extent/");
  return res.data;
}

async function fetchMapPoints(bbox: string): Promise<MapPoint[]> {
  const res = await api.get("/map/points/", { params: { bbox } });
  return res.data;
}

const isVisited = (status: string) => {
  const normalized = (status ?? "").toLowerCase();
  return normalized === "visited" || normalized === "regular" || normalized === "customer";
};

export default function ReportsCoverageDeck() {
  const { user, loading: authLoading } = useAuth();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapLibreMap | null>(null);
  const overlayRef = useRef<MapboxOverlay | null>(null);
  const [bbox, setBbox] = useState<string>("");
  const [zoomLevel, setZoomLevel] = useState<number>(8);

  const extentQuery = useQuery({
    queryKey: ["coverage-extent"],
    queryFn: fetchMapExtent,
    staleTime: 5 * 60_000,
  });

  const debouncedUpdateBounds = useMemo(
    () =>
      debounce(() => {
        const map = mapRef.current;
        if (!map) return;
        const bounds = map.getBounds();
        const west = bounds.getWest();
        const south = bounds.getSouth();
        const east = bounds.getEast();
        const north = bounds.getNorth();
        setBbox(`${west},${south},${east},${north}`);
        setZoomLevel(map.getZoom());
      }, 250),
    []
  );

  useEffect(() => {
    return () => debouncedUpdateBounds.cancel();
  }, [debouncedUpdateBounds]);

  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const map = new maplibregl.Map({
      container: mapContainerRef.current,
      style: BASEMAP_STYLE,
      center: [-3.436, 55.378],
      zoom: 6,
      maxZoom: 18,
      attributionControl: false,
    });

    map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-right");
    map.addControl(new maplibregl.AttributionControl({ compact: true }), "bottom-left");

    const overlay = new MapboxOverlay({ interleaved: true, layers: [] });
    map.addControl(overlay);

    mapRef.current = map;
    overlayRef.current = overlay;
    setZoomLevel(map.getZoom());

    map.on("moveend", debouncedUpdateBounds);
    map.on("zoomend", () => setZoomLevel(map.getZoom()));
  }, [debouncedUpdateBounds]);

  useEffect(() => {
    const map = mapRef.current;
    const overlay = overlayRef.current;
    if (!map || !overlay) return;

    return () => {
      overlay.setProps({ layers: [] });
      overlay.finalize?.();
      map.remove();
      mapRef.current = null;
      overlayRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    const bounds = extentQuery.data;
    if (!map || !bounds) return;

    const fitBounds: LngLatBoundsLike = [
      [bounds.min_lng, bounds.min_lat],
      [bounds.max_lng, bounds.max_lat],
    ];

    map.fitBounds(fitBounds, { padding: 24, duration: 0 });
    setZoomLevel(map.getZoom());
    debouncedUpdateBounds();
  }, [extentQuery.data, debouncedUpdateBounds]);

  const pointsQuery = useQuery<MapPoint[]>({
    queryKey: ["coverage-points", bbox],
    queryFn: () => fetchMapPoints(bbox),
    enabled: !!bbox,
    refetchOnWindowFocus: false,
    placeholderData: (prev) => prev,
  });

  const clusterIndex = useMemo(() => {
    const points = pointsQuery.data ?? [];
    if (!points.length) return null;
    const index = new Supercluster<PointProps>({
      radius: 80,
      maxZoom: 18,
    });
    index.load(
      points.map((point) => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [point.longitude, point.latitude] },
        properties: { ...point, visited: isVisited(point.status) },
      }))
    );
    return index;
  }, [pointsQuery.data]);

  const clusters = useMemo<ClusterLike[]>(() => {
    const map = mapRef.current;
    if (!map || !clusterIndex) return [];
    const bounds = map.getBounds();
    const bboxArray: [number, number, number, number] = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth(),
    ];
    return clusterIndex.getClusters(bboxArray, Math.round(map.getZoom())) as ClusterLike[];
  }, [clusterIndex, zoomLevel]);

  const buildLayers = useCallback(() => {
    const points = pointsQuery.data ?? [];
    const layers: Layer[] = [];

    if (!points.length) return layers;

    if (zoomLevel < HEATMAP_MAX_ZOOM) {
      layers.push(
        new HeatmapLayer<MapPoint>({
          id: "coverage-heatmap",
          data: points,
          getPosition: (p: MapPoint) => [p.longitude, p.latitude],
          getWeight: (p: MapPoint) => (isVisited(p.status) ? 1.0 : 0.4),
          radiusPixels: 50,
          intensity: 1.2,
          threshold: 0.05,
        })
      );
    } else if (zoomLevel < CLUSTER_MAX_ZOOM) {
      layers.push(
        new ScatterplotLayer<ClusterLike>({
          id: "coverage-clusters",
          data: clusters,
          pickable: true,
          radiusUnits: "pixels",
          getPosition: (f: ClusterLike) => f.geometry.coordinates as [number, number],
          getRadius: (f: ClusterLike) => {
            const props = (f as any).properties;
            if (props?.cluster) {
              const count = props.point_count as number;
              const digits = String(count).length;
              const baseRadius = 10 + digits * 3;
              const scaledRadius = 8 + Math.sqrt(Math.max(count, 1)) * 2.8;
              return Math.min(60, Math.max(baseRadius, scaledRadius));
            }
            return 8;
          },
          getFillColor: (f: ClusterLike) => {
            const props = (f as any).properties;
            if (props?.cluster) return CLUSTER_COLOR;
            const visited = Boolean(props?.visited);
            return visited ? VISITED_COLOR : NOT_VISITED_COLOR;
          },
          radiusMinPixels: 10,
          radiusMaxPixels: 80,
          stroked: true,
          getLineColor: [255, 255, 255, 140],
          lineWidthMinPixels: 1.5,
        })
      );

      layers.push(
        new TextLayer<ClusterLike>({
          id: "coverage-cluster-labels",
          data: clusters.filter((c) => Boolean((c.properties as any).cluster)),
          getPosition: (f: ClusterLike) => f.geometry.coordinates as [number, number],
          getText: (f: ClusterLike) => String((f.properties as any).point_count),
          getSize: 13,
          fontWeight: 700,
          getColor: [255, 255, 255, 255],
          outlineColor: [0, 0, 0, 220],
          outlineWidth: 1.5,
          getTextAnchor: "middle",
          getAlignmentBaseline: "center",
          fontFamily: "Inter, system-ui, sans-serif",
        })
      );
    } else {
      layers.push(
        new ScatterplotLayer<MapPoint>({
          id: "coverage-points",
          data: points,
          pickable: true,
          radiusUnits: "pixels",
          getPosition: (p: MapPoint) => [p.longitude, p.latitude],
          getRadius: 9,
          radiusMinPixels: 4,
          radiusMaxPixels: 16,
          getFillColor: (p: MapPoint) => (isVisited(p.status) ? VISITED_COLOR : NOT_VISITED_COLOR),
          stroked: true,
          getLineColor: [255, 255, 255, 120],
          lineWidthMinPixels: 1,
        })
      );
    }

    return layers;
  }, [clusters, pointsQuery.data, zoomLevel]);

  useEffect(() => {
    const overlay = overlayRef.current;
    const map = mapRef.current;
    if (!overlay || !map) return;

    const layers = buildLayers();

    overlay.setProps({
      layers,
      getTooltip: ({ object }: { object: ClusterLike | MapPoint | null }) => {
        if (!object) return null;
        const props = (object as any).properties ?? object;
        if (props.cluster) return `${props.point_count} addresses`;
        const status = props.status ?? "Unknown";
        return `Status: ${status}`;
      },
      onClick: (info: { object: ClusterLike | MapPoint | null; coordinate: [number, number] }) => {
        const obj = info.object as any;
        if (!obj) return;
        const props = obj.properties ?? obj;
        if (props.cluster && clusterIndex) {
          const expansionZoom = clusterIndex.getClusterExpansionZoom(props.cluster_id);
          map.easeTo({
            center: info.coordinate as [number, number],
            zoom: Math.min(expansionZoom, 18),
            duration: 300,
          });
        }
      },
    });
  }, [buildLayers, clusterIndex]);

  if (authLoading) return <LoadingSplash />;
  if (!user?.is_superuser) return <Navigate to="/search" replace />;

  const showMapSpinner = extentQuery.isPending || (pointsQuery.isPending && !pointsQuery.data);

  return (
    <Box sx={{ px: 2, pb: 3 }}>
      <Stack spacing={2}>
        <Paper elevation={1} sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Prototype MapLibre + deck.gl view. Zoom controls switch between heatmap, clusters, and individual points.
          </Typography>
          <Box className="coverage-map-container">
            <div ref={mapContainerRef} className="coverage-map" />
            {showMapSpinner && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(255,255,255,0.65)",
                  zIndex: 500,
                }}
              >
                <CircularProgress size={32} />
              </Box>
            )}
          </Box>
        </Paper>
      </Stack>
    </Box>
  );
}
